import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { getId } from '~utils/data';
import { fromError } from '~utils/errors';
import { ensure } from '~utils/types';
import {
  DeletePowerScheduleModal_PowerScheduleFragment,
  useDeletePowerSchedulesMutation,
} from './__generated__/DeletePowerSchedulesModal.graphql';

interface Props {
  powerSchedules: DeletePowerScheduleModal_PowerScheduleFragment[];
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

export function DeletePowerSchedulesModal({ powerSchedules, isOpen, onCancel, onSuccess }: Props) {
  const isPowerScheduleUsedByDisplays = powerSchedules.some(
    (powerSchedule) => powerSchedule.allDisplays.length > 0,
  );
  const isPowerScheduleUsedByConfigurations = powerSchedules.some(
    (powerSchedule) => powerSchedule.isPowerScheduleUsedByAnyConfiguration,
  );
  const toast = useToast();
  const [deletePowerSchedule] = useDeletePowerSchedulesMutation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { customer } = useParams();

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleGoToConfiguration = useCallback(() => {
    onCancel();
    const customerHandle = ensure(customer);
    navigate(`/${auth.organization?.handle}/customers/${customerHandle}/configurations`);
  }, [onCancel, navigate, auth.organization?.handle, customer]);

  const handleDelete = useCallback(async () => {
    try {
      await deletePowerSchedule({
        variables: {
          input: {
            powerScheduleIds: powerSchedules.map(getId),
          },
        },
      });

      toast({
        status: 'success',
        title: powerSchedules.length === 1 ? t('powerScheduleDeleted') : t('powerSchedulesDeleted'),
        description:
          powerSchedules.length === 1
            ? t('powerScheduleDeletedDesc')
            : t('powerScheduleDeletedDesc1'),
      });

      await onSuccess?.();
    } catch (err) {
      toast({
        status: 'error',
        title: t('powerSchedulesDeleteErr'),
        description: fromError(err, 'DeletePowerSchedules', {
          POWER_SCHEDULE_NOT_FOUND: t('powerSchedulesDeleteErrDesc'),
          POWER_SCHEDULE_IN_USE_ERROR: t('powerSchedulesDeleteErrDesc1'),
        }),
      });
    }
  }, [deletePowerSchedule, onSuccess, powerSchedules, toast]);

  const isPowerScheduleUsedByManyDisplays = powerSchedules.length > 1;

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />

      {isPowerScheduleUsedByDisplays || isPowerScheduleUsedByConfigurations ? (
        <ModalContent>
          <ModalHeader>
            {isPowerScheduleUsedByManyDisplays
              ? t('powerSchedulesDeleteErr1')
              : t('powerSchedulesDeleteErr2')}
          </ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>
              {isPowerScheduleUsedByConfigurations && isPowerScheduleUsedByDisplays
                ? t(
                    isPowerScheduleUsedByManyDisplays
                      ? 'powerScheduleInUseInConfigAndDisplaysError'
                      : 'powerScheduleInUseInConfigAndDisplaysErrorSingle',
                  )
                : isPowerScheduleUsedByConfigurations
                ? t(
                    isPowerScheduleUsedByManyDisplays
                      ? 'powerScheduleDeletionConfigErrorMany'
                      : 'powerScheduleDeletionConfigErrorSingle',
                  )
                : t(
                    isPowerScheduleUsedByManyDisplays
                      ? 'powerScheduleDeletionErrorMany'
                      : 'powerScheduleDeletionErrorSingle',
                  )}
            </Text>
          </ModalBody>
          <ModalFooter>
            {isPowerScheduleUsedByDisplays ? (
              <Button onClick={handleClose} variant="solid" colorScheme="blue">
                {t('ok')}
              </Button>
            ) : (
              <>
                <Button onClick={handleClose} variant="ghost" colorScheme="blue">
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleGoToConfiguration}
                  variant="solid"
                  colorScheme="blue"
                  marginLeft="3"
                >
                  {t('goToConfigurations')}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      ) : (
        <ModalContent>
          <ModalHeader>
            {powerSchedules.length > 1 ? t('deletePowerSchedules') : t('deletePowerSchedule')}
          </ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>
              {t('deletePowerScheduleConfirmation', {
                count: powerSchedules.length,
              })}
            </Text>
            <Text color={'gray.600'} marginTop="6">
              {t('actionCannotBeUndone')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose} variant="ghost" colorScheme="blue">
              {t('cancel')}
            </Button>
            <Button variant="solid" colorScheme="red" marginLeft="3" onClick={handleDelete}>
              {t('delete')}
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Modal>
  );
}

DeletePowerSchedulesModal.graphql = {
  fragments: {
    DeletePowerSchedulesModal_powerSchedule: gql`
      fragment DeletePowerScheduleModal_powerSchedule on PowerSchedule {
        id
        isPowerScheduleUsedByAnyConfiguration
        allDisplays: displays {
          id
        }
      }
    `,
  },
  mutations: {
    DeletePowerSchedules: gql`
      mutation DeletePowerSchedules($input: PowerSchedulesDeleteInput!) {
        powerSchedulesDelete(input: $input) {
          powerScheduleIds
        }
      }
    `,
  },
};
